import type { IFieldOptions } from '@axo/form/feature/form-input';
import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { TextFieldUI } from '@axo/form/feature/form-input/TextField/TextFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { Text } from '@axo/ui-core/components/typography';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../../../context/AxoForm/AxoForm';
import { validateFinnishSSN } from '../../../../../../../utils/Finland/validateFinnishSSN';
import { useEmailValidation } from '../../../../../../../utils/useEmailValidation';
import type {
  CoApplicantDetailsData,
  FinlandFormData,
} from '../../../../../FormData.fi.types';
import { gridColumns } from '../../../../config/layouts/grid';
import {
  getEmailRegisterOptions,
  phoneFieldOptions,
} from '../../../shared-config';

export const CoApplicantDetails = () => {
  const { t } = useTranslation();

  const { baseUrl, axoFormData } = useAxoFormContext<FinlandFormData>();

  const { watch, setValue } = useFormContext();

  const { EmailPrompt, formError, enableEmailValidation } = useEmailValidation({
    baseUrl,
    email: watch('CoCustomer/Email'),
    setEmail: (email) => setValue('CoCustomer/Email', email),
  });

  const emailRegisterOptions = getEmailRegisterOptions(enableEmailValidation);

  const mainApplicantSSN = axoFormData['Person/SocialSecurityNumber'];
  const ssnRegisterOptions: IFieldOptions = useMemo(
    () => ({
      registerOptions: {
        required: 'CoAppSocialSecurityNumber.error.required',
        setValueAs: (value) => value.toLocaleUpperCase().trim(),
        validate: (value) => {
          if (!validateFinnishSSN(value))
            return 'CoAppSocialSecurityNumber.error.pattern';

          if (value === mainApplicantSSN)
            return t('CoAppSocialSecurityNumber.error.unique');
        },
      },
      tooltipValidation: {
        message: 'CoAppSocialSecurityNumber.tooltip',
      },
    }),
    [mainApplicantSSN, t]
  );

  return (
    <>
      <Text>
        <b>{t('AddCoApplicant.CoApplicantDetails')}</b>
      </Text>
      <Grid columns={gridColumns}>
        <EmailFieldUI<CoApplicantDetailsData>
          name="CoCustomer/Email"
          label={t('CoAppEmail.label')}
          data-testid="email-input"
          registerOptions={emailRegisterOptions}
          error={formError}
          confirmationPrompt={<EmailPrompt />}
        />

        <PhoneFieldUI<CoApplicantDetailsData>
          name="CoCustomer/MobilePhoneNumber"
          label={t('CoAppMobilePhoneNumber.label')}
          data-testid="phone-number-input"
          {...phoneFieldOptions}
        />

        <TextFieldUI<CoApplicantDetailsData>
          name="CoPerson/SocialSecurityNumber"
          label={t('CoAppSocialSecurityNumber.label')}
          placeholder={t('SocialSecurityNumber.placeholder')}
          data-testid="ssn-input"
          {...ssnRegisterOptions}
        />
      </Grid>
    </>
  );
};
